import { apiFetch, apiFetchBlob } from "@/lib/api";

export const RMA_STATUSES = [
  "awaiting_return",
  "received",
  "closed",
  "cancelled",
] as const;

export type RmaStatus = (typeof RMA_STATUSES)[number];

export const RMA_LED_BY = ["customer", "end_user"] as const;

export type RmaLedBy = (typeof RMA_LED_BY)[number];

export const QUALITY_RMAS_LIST_KEY = ["quality", "rmas"] as const;

export function rmaStatusLabel(status: RmaStatus): string {
  switch (status) {
    case "awaiting_return":
      return "Awaiting return";
    case "received":
      return "Received";
    case "closed":
      return "Closed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function rmaLedByLabel(ledBy: RmaLedBy): string {
  switch (ledBy) {
    case "customer":
      return "Customer-led";
    case "end_user":
      return "End-user-led";
    default:
      return ledBy;
  }
}

export interface QualityRma {
  id: number;
  rmaNumber: string;
  status: RmaStatus;
  ledBy: RmaLedBy;
  requestedAt: string | null;
  approvedAt: string | null;
  complaint: string | null;
  sgsCauseOfReturn: string | null;
  quantityAndModels: string | null;
  purchaseReference: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  shipFromLine1: string | null;
  shipFromLine2: string | null;
  shipFromCity: string | null;
  shipFromState: string | null;
  shipFromPostalCode: string | null;
  shipFromCountry: string | null;
  shipToLine1: string | null;
  shipToLine2: string | null;
  shipToCity: string | null;
  shipToState: string | null;
  shipToPostalCode: string | null;
  shipToCountry: string | null;
  customerFsAccountId: number | null;
  customerName: string | null;
  endUserFsAccountId: number | null;
  endUserName: string | null;
  createdBy: string;
  createdByDisplay: string | null;
  createdAt: string;
  updatedAt: string;
  intakeCount: number;
  investigationCount: number;
  receiveQuantityDiscrepancy: boolean;
  salesOrders: Array<{ salesOrderId: number; salesOrderNumber: string | null }>;
}

export interface QualityRmaApproval {
  approvalTicketId: number;
  name: string;
  type: string;
  columnKey: string;
  referenceNumber: string | null;
  salesOrderId: number | null;
  salesOrderNumber: string | null;
}

export interface QualityRmaDetailResponse {
  rma: QualityRma;
  salesOrders: Array<{ salesOrderId: number; salesOrderNumber: string | null }>;
  approvals: QualityRmaApproval[];
  freshdeskTicketIds: number[];
  devices: Array<{
    id: number;
    deviceId: number | null;
    serialFciId: number | null;
    deviceLabelSerial: string;
    finishedSku: string | null;
    firmwareVersion: string | null;
    communicating: boolean;
    pcbaLocationId: number | null;
    qualityIncidentId: number | null;
    qualityIncidentRepairScrap: string | null;
    qualityIncidentOpen: boolean;
  }>;
  rcas: Array<{
    id: number;
    rmaId: number | null;
    status: "open" | "closed";
    openedAt: string;
    openedBy: string;
    openedByDisplay: string | null;
    openedByAvatarUrl: string | null;
    openedAutomatically: boolean;
    summary: string | null;
    form: Record<string, unknown> | null;
  }>;
  intake: Array<{
    id: number;
    receivedAt: string | null;
    unitGroup: string | null;
    unitModel: string | null;
    unitSerial: string | null;
    pcbaLocationId: number | null;
    visualInspection: string | null;
    dateOfManufacture: string | null;
    aaVoltage: string | null;
    cVoltage: string | null;
    hybridCap: string | null;
    firmwareVersion: string | null;
    boardRevision: string | null;
    loggedBy: string | null;
    boxLabel: string | null;
    rcaDate: string | null;
    rcaNotes: string | null;
    resolution: string | null;
    analyzedBy: string | null;
    resolutionDate: string | null;
    resolvedBy: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  investigations: Array<{
    investigationId: number;
    qualityCaseId: number;
    investigationStatus: string;
    caseTitle: string;
  }>;
  rcaLinkedRmas?: Array<{
    rcaId: number;
    id: number;
    rmaNumber: string;
    status: RmaStatus;
    customerName: string | null;
    contactName: string | null;
    contactPhone: string | null;
    dateReceived: string | null;
    quantityAndModels: string | null;
    complaint: string | null;
  }>;
  upsLabels: Array<{
    id: number;
    trackingNumber: string;
    serviceCode: string | null;
    createdBy: string;
    createdAt: string;
    voidedAt: string | null;
  }>;
  startedQualityCaseId?: number;
  startedInvestigationId?: number;
  startedApprovalTicketId?: number;
}

export interface QualityCustomerSearchRow {
  fsAccountId: number;
  name: string | null;
  endUserName: string | null;
}

export async function fetchQualityRmas(input: {
  search?: string;
  status?: RmaStatus;
  ledBy?: RmaLedBy;
  limit?: number;
}): Promise<{ rows: QualityRma[]; total: number }> {
  return apiFetch<{ rows: QualityRma[]; total: number }>("/api/quality/rmas", {
    query: {
      search: input.search,
      status: input.status,
      ledBy: input.ledBy,
      limit: input.limit != null ? String(input.limit) : undefined,
    },
  });
}

export async function resolveQualityRmaArrivalScan(
  scan: string,
): Promise<{ rma: { id: number; rmaNumber: string } | null }> {
  return apiFetch("/api/quality/rmas/resolve-scan", { query: { scan } });
}

export function qualityRmaQueryKey(id: number): readonly unknown[] {
  return ["quality", "rma", id];
}

export async function fetchQualityRmaDetail(id: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${id}`);
}

export interface CreateQualityRmaInput {
  ledBy?: RmaLedBy;
  requestedAt?: string | null;
  approvedAt?: string | null;
  complaint?: string | null;
  sgsCauseOfReturn?: string | null;
  quantityAndModels?: string | null;
  purchaseReference?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  shipFromLine1?: string | null;
  shipFromLine2?: string | null;
  shipFromCity?: string | null;
  shipFromState?: string | null;
  shipFromPostalCode?: string | null;
  shipFromCountry?: string | null;
  shipToLine1?: string | null;
  shipToLine2?: string | null;
  shipToCity?: string | null;
  shipToState?: string | null;
  shipToPostalCode?: string | null;
  shipToCountry?: string | null;
  customerFsAccountId?: number | null;
  endUserFsAccountId?: number | null;
  salesOrderIds?: number[];
  approvalTicketIds?: number[];
  startReplacementApproval?: boolean;
  freshdeskTicketIds?: number[];
  deviceLabels?: string[];
  qualityEventId?: number | null;
}

export async function createQualityRma(input: CreateQualityRmaInput): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>("/api/quality/rmas", {
    method: "POST",
    body: (input),
  });
}

export async function updateQualityRma(
  id: number,
  input: Partial<CreateQualityRmaInput> & { status?: RmaStatus },
): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${id}`, {
    method: "PATCH",
    body: (input),
  });
}

export async function fetchQualityCustomerQuickSearch(
  query: string,
): Promise<{ rows: QualityCustomerSearchRow[] }> {
  return apiFetch<{ rows: QualityCustomerSearchRow[] }>("/api/quality/customers/quick-search", {
    query: { q: query },
  });
}

export interface QualitySavedAddress {
  id: string;
  label: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string | null;
}

export async function fetchQualitySavedAddresses(input: {
  customerFsAccountId: number | null;
  endUserFsAccountId: number | null;
}): Promise<{ rows: QualitySavedAddress[] }> {
  return apiFetch<{ rows: QualitySavedAddress[] }>("/api/quality/customers/saved-addresses", {
    query: {
      customerFsAccountId: input.customerFsAccountId != null ? String(input.customerFsAccountId) : undefined,
      endUserFsAccountId: input.endUserFsAccountId != null ? String(input.endUserFsAccountId) : undefined,
    },
  });
}

export async function addRmaSalesOrder(rmaId: number, salesOrderId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/sales-orders`, {
    method: "POST",
    body: ({ salesOrderId }),
  });
}

export async function removeRmaSalesOrder(rmaId: number, salesOrderId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/sales-orders/${salesOrderId}`, {
    method: "DELETE",
  });
}

export interface QualityApprovalSearchRow {
  id: number;
  name: string;
  type: string;
  columnKey: string;
  referenceNumber: string | null;
  salesOrderId: number | null;
  salesOrderNumber: string | null;
}

export async function fetchQualityApprovalQuickSearch(
  query: string,
): Promise<{ rows: QualityApprovalSearchRow[] }> {
  return apiFetch<{ rows: QualityApprovalSearchRow[] }>("/api/quality/approvals/quick-search", {
    query: { q: query, limit: "20" },
  });
}

export interface QualityFreshdeskTicketSearchRow {
  id: number;
  subject: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
  url: string;
}

export async function fetchQualityFreshdeskTicketQuickSearch(
  query: string,
): Promise<{ rows: QualityFreshdeskTicketSearchRow[] }> {
  return apiFetch<{ rows: QualityFreshdeskTicketSearchRow[] }>("/api/quality/freshdesk-tickets/quick-search", {
    query: { q: query, limit: "20" },
  });
}

export async function addRmaApproval(rmaId: number, approvalTicketId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/approvals`, {
    method: "POST",
    body: ({ approvalTicketId }),
  });
}

export async function removeRmaApproval(rmaId: number, approvalTicketId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/approvals/${approvalTicketId}`, {
    method: "DELETE",
  });
}

export async function startRmaReplacementApproval(rmaId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/approvals/start`, {
    method: "POST",
    body: ({}),
  });
}

export async function addRmaFreshdeskTicket(rmaId: number, ticketId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/freshdesk-tickets`, {
    method: "POST",
    body: ({ ticketId }),
  });
}

export async function removeRmaFreshdeskTicket(rmaId: number, ticketId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/freshdesk-tickets/${ticketId}`, {
    method: "DELETE",
  });
}

export async function addRmaDevice(rmaId: number, deviceLabelSerial: string): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/devices`, {
    method: "POST",
    body: ({ deviceLabelSerial }),
  });
}

export async function removeRmaDevice(rmaId: number, deviceRowId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/devices/${deviceRowId}`, {
    method: "DELETE",
  });
}

export async function updateRmaIntake(
  rmaId: number,
  intakeId: number,
  input: {
    unitSerial?: string;
    unitModel?: string;
    visualInspection?: string | null;
    aaVoltage?: string | null;
    cVoltage?: string | null;
    boardRevision?: string | null;
    firmwareVersion?: string | null;
    resolution?: string | null;
  },
): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/intake/${intakeId}`, {
    method: "PATCH",
    body: input,
  });
}

export async function addRmaIntake(
  rmaId: number,
  input: {
    unitSerial?: string;
    unitModel?: string;
    unitGroup?: string;
    visualInspection?: string;
    boxLabel?: string;
    aaVoltage?: string;
    cVoltage?: string;
    boardRevision?: string;
    firmwareVersion?: string;
  },
): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/intake`, {
    method: "POST",
    body: (input),
  });
}

export async function linkRmaInvestigation(
  rmaId: number,
  investigationId: number,
): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/investigations`, {
    method: "POST",
    body: ({ investigationId }),
  });
}

export async function fetchPcbaSkus(query: string): Promise<{ rows: Array<{ sku: string; description: string | null }> }> {
  return apiFetch("/api/quality/pcba-skus", { query: { q: query } });
}

/** Opens a new RCA owned by this RMA. The RCA worksheet itself lives under /api/quality/rcas/:id. */
export async function openRmaRca(
  rmaId: number,
  problemStatement: string | null,
): Promise<QualityRmaDetailResponse & { openedRcaId: number }> {
  return apiFetch<QualityRmaDetailResponse & { openedRcaId: number }>(`/api/quality/rmas/${rmaId}/rca`, {
    method: "POST",
    body: problemStatement == null || problemStatement.trim() === "" ? {} : { problemStatement: problemStatement.trim() },
  });
}

export async function linkRmaRca(rmaId: number, rcaId: number): Promise<QualityRmaDetailResponse> {
  return apiFetch<QualityRmaDetailResponse>(`/api/quality/rmas/${rmaId}/rcas`, {
    method: "POST",
    body: ({ rcaId }),
  });
}

export async function createRmaUpsReturnLabel(
  rmaId: number,
  input: {
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
    serviceCode?: string;
    quantity?: number;
  },
): Promise<{
  tracking: string;
  labels: Array<{
    tracking_no: string | null;
    img_base64: string | null;
    letter_pdf_base64: string | null;
    label_4x6_pdf_base64: string | null;
  }>;
  detail: QualityRmaDetailResponse;
}> {
  return apiFetch(`/api/quality/rmas/${rmaId}/ups-return-label`, {
    method: "POST",
    body: (input),
  });
}

export async function downloadRmaDocument(
  rmaId: number,
  rmaNumber: string,
  kind: "customer" | "internal" = "customer",
): Promise<void> {
  const query = kind === "internal" ? "?kind=internal" : "";
  const { blob } = await apiFetchBlob(`/api/quality/rmas/${rmaId}/document${query}`);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = kind === "internal" ? `${rmaNumber}-internal.pdf` : `${rmaNumber}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function freshdeskTicketUrl(ticketId: number): string {
  return `https://smartgridsolutions.freshdesk.com/a/tickets/${ticketId}`;
}
